import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import AuthLayout, { AuthLink } from "@/components/AuthLayout";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      navigate(data.role === "admin" ? "/admin" : "/publisher");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      eyebrow="Access / Sign In"
      title="Welcome back"
      subtitle="Sign in to your publisher or admin account."
      footer={
        <p className="text-sm text-slate-500 text-center">
          Publisher?{" "}
          <AuthLink to="/signup" data-testid="signup-link">
            Create an account
          </AuthLink>
        </p>
      }
    >
      <div data-testid="login-page" />
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div
            className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
            data-testid="login-error"
          >
            {error}
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">
            Email
          </Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="h-11"
            data-testid="login-email-input"
          />
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label
              htmlFor="password"
              className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold"
            >
              Password
            </Label>
            <Link
              to="/forgot-password"
              className="text-xs text-slate-500 hover:text-[#002FA7] transition-colors"
              data-testid="forgot-password-link"
            >
              Forgot?
            </Link>
          </div>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="h-11"
            data-testid="login-password-input"
          />
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full h-11 bg-[#002FA7] hover:bg-[#00227A] text-white font-medium tracking-tight"
          data-testid="login-submit-btn"
        >
          {loading ? "Signing in…" : "Sign In"}
        </Button>
      </form>
    </AuthLayout>
  );
}
